import type { ClassValue } from 'clsx';
import type { CSSProperties, FC, ReactElement } from 'react';
import { Rating as SmastromRating } from '@smastrom/react-rating';
import type { RatingProps as SmastromRatingProps } from '@smastrom/react-rating';
import '@smastrom/react-rating/style.css';
import { twcls } from '@utils/twcls';
import './index.scss';
// Скруглённая 5-конечная звезда (Tabler star-filled) — воспроизводит images/icons/star.png
const StarShape: ReactElement<any> = (
  <path d='M8.243 7.34l-6.38 .925l-.113 .023a1 1 0 0 0 -.44 1.684l4.622 4.499l-1.09 6.355l-.013 .11a1 1 0 0 0 1.464 .944l5.706 -3l5.693 3l.1 .046a1 1 0 0 0 1.352 -1.1l-1.091 -6.355l4.624 -4.5l.078 -.085a1 1 0 0 0 -.633 -1.62l-6.38 -.926l-2.852 -5.78a1 1 0 0 0 -1.794 0l-2.853 5.78z' />
);

// Кружок — воспроизводит индикаторы сложности .stage / .difficult
const CircleShape: ReactElement<any> = <circle cx='12' cy='12' r='11' />;

export interface RatingProps {
  /** Совместимость со старым react-rating API */
  initialRating?: number;
  /** Альтернатива initialRating (для контролируемого режима) */
  value?: number;
  readonly?: boolean;
  onChange?: (value: number) => void;
  /** Кол-во элементов (по умолчанию 5, как в react-rating) */
  count?: number;
  /** Вариант фигуры: звезда (по умолчанию) или кружок (сложность) */
  variant?: 'star' | 'circle';
  /** Размер одного элемента в px (для расчёта ширины контейнера) */
  size?: number;
  activeColor?: string;
  inactiveColor?: string;
  className?: ClassValue;
  style?: CSSProperties;

  emptySymbol?: ReactElement<any>;
  fullSymbol?: ReactElement<any>;
}

const DEFAULTS = {
  star: { active: '#FC9A11', inactive: '#C4C4C4', shape: StarShape, size: 16 },
  circle: {
    active: '#92022D',
    inactive: '#C4C4C4',
    shape: CircleShape,
    size: 12
  }
} as const;

const Rating: FC<RatingProps> = ({
  initialRating,
  value,
  readonly = false,
  onChange,
  count = 5,
  variant = 'star',
  size,
  activeColor,
  inactiveColor,
  className,
  style
}) => {
  const preset = DEFAULTS[variant];
  const itemSize = size ?? preset.size;
  const ratingValue = value ?? initialRating ?? 0;

  return (
    <SmastromRating
      className={twcls(className) || undefined}
      style={{ maxWidth: count * itemSize + (count - 1) * 4, ...style }}
      value={ratingValue}
      items={count as SmastromRatingProps['items']}
      readOnly={readonly}
      onChange={onChange}
      itemStyles={{
        itemShapes: preset.shape,
        activeFillColor: activeColor ?? preset.active,
        inactiveFillColor: inactiveColor ?? preset.inactive
      }}
    />
  );
};

export default Rating;
